import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { LuUpload } from 'react-icons/lu';
import { IoDuplicateSharp } from 'react-icons/io5';
import { IoIosSend } from 'react-icons/io';
import { Tooltip } from 'react-tooltip';
import EtaiementNotesForm from '~/components/etaiement/form/EtaiementNotesForm';
import styles from './general-sidebar.module.scss';
import type { HistoryItem, IEtaiement } from '~/types/etaiements';
import Modal from '~/lib/Modal';
import ModalExport from '../ModalExport';
import { useCreateEtaiementVersion, useGetEtaiementsList } from '~/hooks/etaiements';
import { IOption } from '~/types/options';
import EtaiementCallbackForm from '../form/EtaiementCallbackForm';
import CostBreakdownChart from '../CostBreakdownChart';
import DuplicateEtaiement from '../form/DuplicateEtaiement';

function formatDate(date : Date) {
  const formattedDate = date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
  const formattedTime = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).replace(':', 'h');

  return `Envoyé le ${formattedDate} à ${formattedTime}`;
}

const GeneralSidebar = ({
  etaiement,
  isPreview,
}: {
  etaiement: IEtaiement,
  isPreview: boolean,
}) => {
  const {
    mutate: createEtaiementVersion,
  } = useCreateEtaiementVersion();

  const { data: list = {} } = useGetEtaiementsList();
  const { status } = list;

  const exportRef = useRef<any>();
  const duplicateRef = useRef<any>();
  const sendRef = useRef<any>();

  const statusColor = status?.find((s: IOption) => s.value === etaiement.status)?.color;

  return (
  <div className={styles.sidebar}>
    {etaiement?._id
      && <div className={styles.actions}>
        {!isPreview
          && <button
            onClick={() => sendRef.current.open()}
          >
            {IoIosSend({})}Envoyer
          </button>
        }
        <button
          onClick={() => exportRef.current.open()}
        >
          {LuUpload({})}Exporter
        </button>
        <button
          className={styles.copy}
          data-tooltip-id="duplicate"
          data-tooltip-content="Dupliquer le devis"
          data-tooltip-place="left"
          onClick={() => duplicateRef.current.open()}
        >
          {IoDuplicateSharp({})}
        </button>
        <Tooltip id="duplicate" />
      </div>
    }
    <hr />
    <div className={styles.infos}>
      <p>
        Nom du client&nbsp;:&nbsp;
        <span>{etaiement.client.name}</span>
      </p>
      <p>
        Commercial&nbsp;:&nbsp;
        <span>{etaiement.author.profile?.firstName} {etaiement.author.profile?.lastName}</span>
      </p>
    </div>
    <hr />
    <div className={styles.notes}>
      <EtaiementNotesForm
        etaiementId={etaiement._id}
        notes={etaiement.notes}
      />
    </div>
    <hr />
    <div className={styles.notes}>
      <EtaiementCallbackForm
        etaiementId={etaiement._id}
        callbacks={etaiement.callbacks || []}
      />
    </div>
    <hr />
    <div className={styles.notes}>
      <CostBreakdownChart phases={etaiement.phases} />
    </div>
    <hr />
    {(etaiement?.status === 'refused' || etaiement.history?.length > 0)
      && <div className={styles.history}>
      <div className={styles.form}>
        <div className={styles.header}>
          <h4>Historique</h4>
          {etaiement?.status === 'refused' && !etaiement.parent
            && <button
              type="button"
              onClick={() => createEtaiementVersion(etaiement?._id)}
            >
              Créer une nouvelle version
            </button>
          }
        </div>
        {etaiement.history.map((h: HistoryItem) => <Link
          to={`/etaiements/${h.etaiement}`}
          className={etaiement._id === h.etaiement ? styles.active : ''}
          key={h._id}>
          {etaiement._id === h.etaiement
          && <div className={styles.puce}
            style={{ backgroundColor: statusColor }}
          >

          </div>
          }
          <p>{h.name}</p>
          <p>{h.name !== 'Version actuelle' && formatDate(new Date(h.sendAt))}</p>
        </Link>)}
      </div>
    </div>
    }
    <Modal ref={exportRef} width={700}>
      <ModalExport
        etaiement={etaiement}
        close={() => exportRef?.current.close()}
      />
    </Modal>
    <Modal ref={sendRef} width={700}>
      <ModalExport
        title='Envoyer le devis'
        etaiement={etaiement}
        close={() => sendRef?.current.close()}
      />
    </Modal>
    <Modal ref={duplicateRef} width={400}>
      <DuplicateEtaiement
        etaiementId={etaiement._id}
        clientId={etaiement?.client?._id}
        close={() => duplicateRef?.current.close()}
      />
    </Modal>
  </div>
  );
};

export default GeneralSidebar;
